import React, { useState } from 'react';
import { sans, green, useTheme } from '../../tokens';

const inputStyle = (t) => ({
  fontFamily: sans,
  fontSize: 12,
  padding: '8px 12px',
  border: '1px solid ' + t.border,
  borderRadius: 6,
  background: t.inputBg,
  color: t.textPrimary,
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
});

const labelStyle = (t) => ({
  fontSize: 11,
  fontWeight: 600,
  color: t.textSecondary,
  fontFamily: sans,
  marginBottom: 6,
  display: 'block',
});

const sectionTitleStyle = (t) => ({
  fontSize: 12,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  color: t.textMuted,
  fontFamily: sans,
  marginBottom: 16,
  paddingBottom: 8,
  borderBottom: '1px solid ' + t.border,
});

const THEME_OPTIONS = [
  { value: 'light', label: 'Light', bg: '#FFFFFF', fg: '#212121', accent: '#1D9E75' },
  { value: 'dark', label: 'Dark', bg: '#0F1117', fg: '#E8E8ED', accent: '#1D9E75' },
  { value: 'system', label: 'System', bg: 'linear-gradient(135deg, #FFFFFF 50%, #0F1117 50%)', fg: '#757575', accent: '#1D9E75' },
];

export default function AppearanceSettings({ isDark, setIsDark, lang, setLang }) {
  const t = useTheme();
  const savedAppearance = JSON.parse(localStorage.getItem('finkloz_appearance') || 'null');
  const [theme, setTheme] = useState(() => {
    if (isDark) return 'dark';
    const stored = localStorage.getItem('finkloz_theme');
    if (stored === 'system') return 'system';
    return 'light';
  });
  const [language, setLanguage] = useState(lang || 'en');
  const [density, setDensity] = useState(savedAppearance?.density || 'comfortable');
  const [landingPage, setLandingPage] = useState(savedAppearance?.landingPage || 'dashboard');
  const [sidebar, setSidebar] = useState(savedAppearance?.sidebar || 'expanded');
  const [saved, setSaved] = useState(false);
  const [dirty, setDirty] = useState(false);

  const markDirty = () => setDirty(true);

  const handleSave = () => {
    // Apply theme to app
    if (theme === 'system') {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setIsDark(prefersDark);
      localStorage.setItem('finkloz_theme', 'system');
    } else {
      setIsDark(theme === 'dark');
      localStorage.setItem('finkloz_theme', theme);
    }
    // Apply language to app
    setLang(language);
    localStorage.setItem('closely_lang', language);
    // Save other appearance prefs
    localStorage.setItem('finkloz_appearance', JSON.stringify({
      density, landingPage, sidebar,
    }));
    setSaved(true);
    setDirty(false);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <div style={{ maxWidth: 640 }}>
      <h2 style={{ fontSize: 16, fontWeight: 700, color: t.textPrimary, fontFamily: sans, margin: '0 0 4px' }}>
        Appearance
      </h2>
      <p style={{ fontSize: 12, color: t.textMuted, fontFamily: sans, margin: '0 0 24px' }}>
        Theme, language, layout density, and navigation preferences.
      </p>

      {/* Theme */}
      <div style={sectionTitleStyle(t)}>Theme</div>

      <div style={{ display: 'flex', gap: 16, marginBottom: 32 }}>
        {THEME_OPTIONS.map(opt => (
          <div
            key={opt.value}
            onClick={() => { setTheme(opt.value); markDirty(); }}
            style={{
              flex: 1,
              border: theme === opt.value ? '2px solid ' + green : '1px solid ' + t.border,
              borderRadius: 8,
              padding: 16,
              cursor: 'pointer',
              textAlign: 'center',
              transition: 'border-color 0.15s',
            }}
          >
            <div style={{
              width: 48,
              height: 32,
              borderRadius: 6,
              margin: '0 auto 10px',
              background: opt.bg,
              border: '1px solid ' + t.border,
              position: 'relative',
              overflow: 'hidden',
            }}>
              <div style={{
                position: 'absolute',
                bottom: 4,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 16,
                height: 3,
                borderRadius: 2,
                background: opt.accent,
              }} />
            </div>
            <div style={{ fontSize: 12, fontWeight: 600, color: t.textPrimary, fontFamily: sans }}>
              {opt.label}
            </div>
          </div>
        ))}
      </div>

      {/* Language */}
      <div style={sectionTitleStyle(t)}>Language</div>

      <div style={{ display: 'flex', gap: 16, marginBottom: 32 }}>
        {[
          { value: 'en', label: 'English' },
          { value: 'fr', label: 'Francais' },
        ].map(opt => (
          <label key={opt.value} style={{
            display: 'flex', alignItems: 'center', gap: 8,
            fontSize: 12, fontFamily: sans, color: t.textPrimary, cursor: 'pointer',
          }}>
            <input
              type="radio"
              name="language"
              value={opt.value}
              checked={language === opt.value}
              onChange={() => { setLanguage(opt.value); markDirty(); }}
              style={{ accentColor: green }}
            />
            {opt.label}
          </label>
        ))}
      </div>

      {/* Dashboard Density */}
      <div style={sectionTitleStyle(t)}>Dashboard Density</div>

      <div style={{ display: 'flex', gap: 16, marginBottom: 32 }}>
        {[
          { value: 'compact', label: 'Compact' },
          { value: 'comfortable', label: 'Comfortable' },
          { value: 'spacious', label: 'Spacious' },
        ].map(opt => (
          <label key={opt.value} style={{
            display: 'flex', alignItems: 'center', gap: 8,
            fontSize: 12, fontFamily: sans, color: t.textPrimary, cursor: 'pointer',
          }}>
            <input
              type="radio"
              name="density"
              value={opt.value}
              checked={density === opt.value}
              onChange={() => { setDensity(opt.value); markDirty(); }}
              style={{ accentColor: green }}
            />
            {opt.label}
          </label>
        ))}
      </div>

      {/* Default Landing Page */}
      <div style={sectionTitleStyle(t)}>Default Landing Page</div>

      <div style={{ marginBottom: 32 }}>
        <label style={labelStyle(t)}>Landing page per role</label>
        <select
          value={landingPage}
          onChange={e => { setLandingPage(e.target.value); markDirty(); }}
          style={{ ...inputStyle(t), maxWidth: 300 }}
        >
          <option value="dashboard">Dashboard</option>
          <option value="investor">Investor Portal</option>
          <option value="close">Close & Review</option>
        </select>
      </div>

      {/* Sidebar */}
      <div style={sectionTitleStyle(t)}>Sidebar Default</div>

      <div style={{ display: 'flex', gap: 16, marginBottom: 32 }}>
        {[
          { value: 'expanded', label: 'Expanded' },
          { value: 'collapsed', label: 'Collapsed' },
        ].map(opt => (
          <label key={opt.value} style={{
            display: 'flex', alignItems: 'center', gap: 8,
            fontSize: 12, fontFamily: sans, color: t.textPrimary, cursor: 'pointer',
          }}>
            <input
              type="radio"
              name="sidebar"
              value={opt.value}
              checked={sidebar === opt.value}
              onChange={() => { setSidebar(opt.value); markDirty(); }}
              style={{ accentColor: green }}
            />
            {opt.label}
          </label>
        ))}
      </div>

      {/* Save */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          onClick={handleSave}
          disabled={!dirty}
          style={{
            padding: '10px 24px',
            border: 'none',
            borderRadius: 6,
            background: dirty ? green : t.border,
            color: dirty ? '#fff' : t.textMuted,
            fontSize: 13,
            fontWeight: 600,
            fontFamily: sans,
            cursor: dirty ? 'pointer' : 'default',
            transition: 'all 0.15s',
          }}
        >
          Save
        </button>
        {saved && (
          <span style={{ fontSize: 12, color: green, fontFamily: sans, fontWeight: 500 }}>
            Settings saved
          </span>
        )}
      </div>
    </div>
  );
}
